"use client"

import { useEffect, useState } from "react"
import { Container, Card, Button, Table, Form, Modal } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { fetchRooms, addRoom, updateRoom, deleteRoom } from "../../../redux/slices/roomsSlice"
import { useTranslation } from "../../../hooks/use-translation"
import { Search, Edit, Trash2 } from "lucide-react"
import { useToast } from "../../../components/ui/use-toast"
import { fetchBuildings } from "../../../redux/slices/buildingsSlice"
import { PlusButton } from "../../../components/ui/plus-button"
import "../../../styles/admin-buttons.css"

export default function AdminRoomsPage() {
  const dispatch = useAppDispatch()
  const { rooms = [], isLoading } = useAppSelector((state) => state.rooms)
  const { buildings = [] } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState("")
  const [buildingFilter, setBuildingFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState<any>(null)
  const [formData, setFormData] = useState({
    building: "",
    number: "",
    type: "",
    size: "",
    price: "",
    available: "",
  })

  useEffect(() => {
    dispatch(
      fetchRooms({
        building: buildingFilter !== "all" ? buildingFilter : undefined,
        type: typeFilter !== "all" ? typeFilter : undefined,
      }),
    )
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch, buildingFilter, typeFilter])

  const handleCreateRoom = async () => {
    if (
      !formData.building ||
      !formData.number ||
      !formData.type ||
      !formData.size ||
      !formData.price ||
      !formData.available
    ) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        addRoom({
          building: formData.building,
          number: formData.number,
          type: formData.type,
          size: formData.size,
          price: Number(formData.price),
          available: formData.available,
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Room added successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add room",
        variant: "destructive",
      })
    }
  }

  const handleUpdateRoom = async () => {
    if (
      !formData.building ||
      !formData.number ||
      !formData.type ||
      !formData.size ||
      !formData.price ||
      !formData.available
    ) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        updateRoom({
          roomId: selectedRoom.id,
          building: formData.building,
          number: formData.number,
          type: formData.type,
          size: formData.size,
          price: Number(formData.price),
          available: formData.available,
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Room updated successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update room",
        variant: "destructive",
      })
    }
  }

  const handleDeleteRoom = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this room?")) {
      try {
        const result = await dispatch(deleteRoom(id)).unwrap()
        toast({
          title: "Success",
          description: "Room deleted successfully",
        })
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete room",
          variant: "destructive",
        })
      }
    }
  }

  const handleEditRoom = (room: any) => {
    setSelectedRoom(room)
    setFormData({
      building: room.building,
      number: room.number,
      type: room.type,
      size: room.size,
      price: room.price.toString(),
      available: room.available,
    })
    setIsEditing(true)
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      building: "",
      number: "",
      type: "",
      size: "",
      price: "",
      available: "",
    })
    setIsEditing(false)
    setSelectedRoom(null)
  }

  const filteredRooms = rooms
    ? rooms.filter(
        (room) =>
          (room.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            room.type.toLowerCase().includes(searchTerm.toLowerCase())) &&
          (buildingFilter === "all" || room.building === buildingFilter) &&
          (typeFilter === "all" || room.type === typeFilter),
      )
    : []

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("roomManagement")}</h1>
        <PlusButton
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          title={t("addRoom")}
        />
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
            <div className="d-flex gap-2">
              <Form.Group>
                <Form.Select value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)}>
                  <option value="all">{t("allBuildings")}</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="all">{t("allTypes")}</option>
                  <option value="studio">{t("studio")}</option>
                  <option value="1-bedroom">{t("oneBedroom")}</option>
                  <option value="2-bedroom">{t("twoBedroom")}</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder={t("searchRooms")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-5"
              />
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-semibold text-gray-700">{t("building")}</th>
                    <th className="fw-semibold text-gray-700">{t("roomNumber")}</th>
                    <th className="fw-semibold text-gray-700">{t("type")}</th>
                    <th className="fw-semibold text-gray-700">{t("size")}</th>
                    <th className="fw-semibold text-gray-700">{t("price")}</th>
                    <th className="fw-semibold text-gray-700">{t("availableFrom")}</th>
                    <th className="fw-semibold text-gray-700">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRooms.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        {t("noRoomsFound")}
                      </td>
                    </tr>
                  ) : (
                    filteredRooms.map((room) => (
                      <tr key={room.id} className="border-bottom">
                        <td className="align-middle">
                          {buildings.find((b) => b.id === room.building)?.name || room.building}
                        </td>
                        <td className="align-middle">{room.number}</td>
                        <td className="align-middle">
                          {room.type === "studio"
                            ? t("studio")
                            : room.type === "1-bedroom"
                              ? t("oneBedroom")
                              : t("twoBedroom")}
                        </td>
                        <td className="align-middle">{room.size}</td>
                        <td className="align-middle">${room.price}/month</td>
                        <td className="align-middle">{new Date(room.available).toLocaleDateString("el-GR")}</td>
                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            <Button
                              className="admin-edit-btn"
                              size="sm"
                              onClick={() => handleEditRoom(room)}
                              title={t("editRoom")}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              className="admin-delete-btn"
                              size="sm"
                              onClick={() => handleDeleteRoom(room.id)}
                              title={t("deleteRoom")}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Room Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? t("editRoom") : t("addRoom")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("building")}</Form.Label>
              <Form.Select
                value={formData.building}
                onChange={(e) => setFormData({ ...formData, building: e.target.value })}
              >
                <option value="">{t("selectBuilding")}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("roomNumber")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder="101"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("type")}</Form.Label>
              <Form.Select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                <option value="">{t("selectType")}</option>
                <option value="studio">{t("studio")}</option>
                <option value="1-bedroom">{t("oneBedroom")}</option>
                <option value="2-bedroom">{t("twoBedroom")}</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("size")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                placeholder="45m²"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("price")}</Form.Label>
              <Form.Control
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="1200"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("availableFrom")}</Form.Label>
              <Form.Control
                type="date"
                value={formData.available}
                onChange={(e) => setFormData({ ...formData, available: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="modal-cancel-btn" onClick={() => setShowModal(false)}>
            {t("cancel")}
          </Button>
          <Button className="modal-primary-btn" onClick={isEditing ? handleUpdateRoom : handleCreateRoom}>
            {isEditing ? t("update") : t("create")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
